import React, { useEffect, useMemo, useState } from "react";
import { scrollToSectionById } from "../../utils/sectionScroll";

const sections = [
  { id: "hero", label: "Home" },
  { id: "news", label: "News" },
  { id: "solutions", label: "Who it's for" },
  { id: "trusted-by", label: "Partners" },
  { id: "cta", label: "Get started" },
];

const STEP = 56;
const DOT_COL = 8;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const LandingSectionTimeline = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const totalHeight = useMemo(() => (sections.length - 1) * STEP, []);

  useEffect(() => {
    const handleScroll = () => {
      const viewportAnchor = window.innerHeight * 0.45;
      let currentIndex = 0;

      const elements = sections
        .map((section) => document.getElementById(section.id))
        .filter(Boolean);

      if (!elements.length) return;

      sections.forEach((section, index) => {
        const element = document.getElementById(section.id);

        if (element && element.getBoundingClientRect().top <= viewportAnchor) {
          currentIndex = index;
        }
      });

      const start = elements[0].offsetTop;
      const end = elements[elements.length - 1].offsetTop;
      const currentPosition = window.scrollY + viewportAnchor;
      const denominator = Math.max(end - start, 1);

      setActiveIndex(currentIndex);
      setProgress(clamp((currentPosition - start) / denominator, 0, 1));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const scrollToSection = (id) => {
    scrollToSectionById(id, { duration: 1300 });
  };

  return (
    <div
      className="fixed left-4 top-1/2 z-40 hidden -translate-y-1/2 lg:block"
      style={{ width: "136px" }}
      aria-label="Homepage section timeline"
    >
      <div className="relative" style={{ height: `${totalHeight}px` }}>
        <div
          className="absolute top-0 bottom-0 bg-slate-200"
          style={{ left: `${DOT_COL}px`, width: "1px", transform: "translateX(-50%)" }}
        />
        <div
          className="absolute top-0 origin-top bg-slate-700"
          style={{
            left: `${DOT_COL}px`,
            width: "1px",
            transform: "translateX(-50%)",
            height: `${progress * 100}%`,
          }}
        />

        {sections.map((section, index) => {
          const isActive = activeIndex === index;
          const isPast = index < activeIndex;
          const dotSize = isActive ? 10 : 6;

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => scrollToSection(section.id)}
              className="group absolute flex items-center text-left"
              style={{
                top: `${index * STEP}px`,
                transform: "translateY(-50%)",
                left: 0,
                width: "100%",
              }}
              aria-label={`Scroll to ${section.label}`}
            >
              <div
                className="relative z-10 rounded-full border transition-all duration-300"
                style={{
                  width: `${dotSize}px`,
                  height: `${dotSize}px`,
                  marginLeft: `${DOT_COL - dotSize / 2}px`,
                  backgroundColor: isActive ? "#1f2937" : isPast ? "#94a3b8" : "#ffffff",
                  borderColor: isActive ? "#1f2937" : isPast ? "#94a3b8" : "#cbd5e1",
                }}
              />
              <span
                className="ml-3 whitespace-nowrap text-xs font-medium transition-all duration-200"
                style={{
                  color: isActive ? "#0f172a" : "#94a3b8",
                  opacity: isActive ? 1 : 0,
                }}
              >
                {section.label}
              </span>
              {!isActive && (
                <span
                  className="absolute whitespace-nowrap text-xs text-slate-400 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                  style={{ left: `${DOT_COL + 14}px` }}
                >
                  {section.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
